"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const vue3_compat_1 = require("@/client/vue3-compat");
const Card_vue_1 = require("@/client/components/card/Card.vue");
const CardOrderStorage_1 = require("@/client/utils/CardOrderStorage");
const PreferencesManager_1 = require("@/client/utils/PreferencesManager");
exports.default = (0, vue3_compat_1.defineComponent)({
    name: 'SortableCards',
    components: {
        Card: Card_vue_1.default,
    },
    props: {
        cards: {
            type: Array,
            required: true,
        },
        playerId: {
            type: String,
            required: true,
        },
    },
    data() {
        const cache = CardOrderStorage_1.CardOrderStorage.getCardOrder(this.playerId);
        const cardOrder = {};
        const keys = Object.keys(cache);
        let max = 0;
        for (const key of keys) {
            if (this.cards.find((card) => card.name === key) !== undefined) {
                cardOrder[key] = cache[key];
                max = Math.max(max, cache[key]);
            }
        }
        max++;
        for (const card of this.cards) {
            if (cardOrder[card.name] === undefined) {
                cardOrder[card.name] = max++;
            }
        }
        return {
            showReorder: false,
            cardOrder: cardOrder,
            dragCard: undefined,
            touchDragCard: undefined,
            touchDragTarget: undefined,
            touchInsertAfter: false,
            touchDropTarget: undefined,
            touchGhost: undefined,
            touchStartX: 0,
            touchStartY: 0,
            touchDragStarted: false,
        };
    },
    mounted() {
        const container = this.$refs.sortableContainer;
        container.addEventListener('touchmove', this.onTouchMove, { passive: false });
        document.addEventListener('touchend', this.onTouchEnd);
    },
    beforeUnmount() {
        const container = this.$refs.sortableContainer;
        container.removeEventListener('touchmove', this.onTouchMove);
        document.removeEventListener('touchend', this.onTouchEnd);
    },
    methods: {
        getSortedCards() {
            return CardOrderStorage_1.CardOrderStorage.getOrdered(this.cardOrder, this.cards);
        },
        onDragStart(source) {
            this.dragCard = source;
        },
        onDragEnd() {
            this.dragCard = undefined;
        },
        onDragOver(source) {
            if (this.dragCard === undefined || source === this.dragCard)
                return;
            if (source === 'end') {
                let max = 0;
                const keys = Object.keys(this.cardOrder);
                for (const key of keys) {
                    max = Math.max(max, this.cardOrder[key]);
                }
                this.cardOrder[this.dragCard] = max + 1;
            }
            else {
                const temp = this.cardOrder[source];
                const keys = Object.keys(this.cardOrder);
                for (const key of keys) {
                    if (this.cardOrder[key] >= temp) {
                        this.cardOrder[key]++;
                    }
                }
                this.cardOrder[this.dragCard] = temp;
            }
            CardOrderStorage_1.CardOrderStorage.updateCardOrder(this.playerId, this.cardOrder);
        },
        onTouchStart(cardName, event) {
            const touch = event.touches[0];
            this.touchDragCard = cardName;
            this.touchStartX = touch.clientX;
            this.touchStartY = touch.clientY;
            this.touchDragStarted = false;
            this.touchDragTarget = undefined;
            this.touchInsertAfter = false;
            this.touchDropTarget = undefined;
        },
        onTouchMove(event) {
            if (!this.touchDragCard)
                return;
            const touch = event.touches[0];
            if (!this.touchDragStarted) {
                const dx = touch.clientX - this.touchStartX;
                const dy = touch.clientY - this.touchStartY;
                if (Math.abs(dx) < 8 && Math.abs(dy) < 8)
                    return;
                this.touchDragStarted = true;
                this.dragCard = this.touchDragCard;
                const container = this.$refs.sortableContainer;
                const cardEl = Array.from(container.querySelectorAll('[data-card-name]'))
                    .find((el) => el.getAttribute('data-card-name') === this.touchDragCard);
                if (cardEl) {
                    const rect = cardEl.getBoundingClientRect();
                    this.touchGhost = cardEl.cloneNode(true);
                    this.touchGhost.style.cssText = `
            position: fixed;
            left: ${rect.left}px;
            top: ${rect.top}px;
            width: ${rect.width}px;
            height: ${rect.height}px;
            pointer-events: none;
            opacity: 0.7;
            z-index: 9999;
          `;
                    document.body.appendChild(this.touchGhost);
                }
            }
            event.preventDefault();
            if (this.touchGhost) {
                const ghostRect = this.touchGhost.getBoundingClientRect();
                this.touchGhost.style.left = `${touch.clientX - ghostRect.width / 2}px`;
                this.touchGhost.style.top = `${touch.clientY - ghostRect.height / 2}px`;
            }
            if (this.touchGhost)
                this.touchGhost.style.visibility = 'hidden';
            const element = document.elementFromPoint(touch.clientX, touch.clientY);
            if (this.touchGhost)
                this.touchGhost.style.visibility = '';
            const wrapper = element?.closest('[data-card-name]');
            if (wrapper) {
                const name = wrapper.getAttribute('data-card-name');
                if (name === this.touchDragCard) {
                    this.touchDragTarget = undefined;
                    this.touchDropTarget = undefined;
                }
                else {
                    this.touchDragTarget = name;
                    const wrapperRect = wrapper.getBoundingClientRect();
                    const onRightHalf = touch.clientX > wrapperRect.left + wrapperRect.width / 2;
                    this.touchInsertAfter = onRightHalf;
                    if (onRightHalf) {
                        const container = this.$refs.sortableContainer;
                        const allWrappers = Array.from(container.querySelectorAll('[data-card-name]'));
                        const idx = allWrappers.indexOf(wrapper);
                        const next = allWrappers[idx + 1];
                        const nextName = next?.getAttribute('data-card-name');
                        if (!next || nextName === null) {
                            this.touchDropTarget = 'end';
                        }
                        else if (nextName === this.touchDragCard) {
                            const after = allWrappers[idx + 2];
                            const afterName = after?.getAttribute('data-card-name');
                            this.touchDropTarget = afterName ?? 'end';
                        }
                        else {
                            this.touchDropTarget = nextName;
                        }
                    }
                    else {
                        this.touchInsertAfter = false;
                        this.touchDropTarget = name;
                    }
                }
            }
            else {
                this.touchDragTarget = undefined;
                this.touchInsertAfter = false;
                const container = this.$refs.sortableContainer;
                const rect = container.getBoundingClientRect();
                this.touchDropTarget = touch.clientX > rect.right ? 'end' : undefined;
            }
        },
        onTouchEnd() {
            if (this.touchDragStarted) {
                if (this.touchDropTarget !== undefined) {
                    this.onDragOver(this.touchDropTarget);
                }
                if (this.touchGhost) {
                    document.body.removeChild(this.touchGhost);
                    this.touchGhost = undefined;
                }
                this.dragCard = undefined;
            }
            this.touchDragCard = undefined;
            this.touchDragTarget = undefined;
            this.touchInsertAfter = false;
            this.touchDropTarget = undefined;
            this.touchDragStarted = false;
        },
        clickMethod(e) {
            if (!this.showReorder)
                return;
            const target = e.currentTarget;
            if (!target)
                return;
            if (target.matches('.sortable-cards *')) {
                const rect = target.getBoundingClientRect();
                const x = (e.clientX - rect.left) / rect.width;
                const direction = x <= 0.25 ? -1.5 : x >= 0.75 ? 1.5 : null;
                if (direction) {
                    const cardTitle = target.querySelector('.card-title');
                    if (cardTitle) {
                        const textContent = cardTitle.textContent;
                        if (textContent) {
                            const thisCard = textContent.trim();
                            this.cardOrder[thisCard] += direction;
                            Object.entries(this.cardOrder)
                                .sort((a, b) => a[1] - b[1])
                                .forEach((entry, i) => {
                                this.cardOrder[entry[0]] = i + 1;
                            });
                            CardOrderStorage_1.CardOrderStorage.updateCardOrder(this.playerId, this.cardOrder);
                        }
                    }
                }
            }
        },
        experimentalUI() {
            return (0, PreferencesManager_1.getPreferences)().experimental_ui;
        },
    },
});
